import type { AnalyticsOverview, CachSapSoSanh, MaChiSo, TheChiSo } from '@qlk/shared';
import {
  CHI_SO_HIEU_SUAT,
  chiSoCanQuyenDoanhThu,
  MO_TA_CHI_SO,
  PHAM_VI_MAC_DINH,
  PHAM_VI_NHAN_CO_DINH,
  SO_DONG_SO_SANH,
  TAB_HIEU_SUAT,
  TAB_HIEU_SUAT_NHAN,
  giaiMaPhamVi,
  tabCanQuyenDoanhThu,
  type TabHieuSuat,
} from '@qlk/shared';
import { Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useKyChung } from '../../../layout/kyChung.js';
import { coQuyen, useDuongDanTrang, useToi } from '../../../lib/auth.js';
import { nhanSoKyTruoc } from '../../../lib/kyTrang.js';
import { useDoiTac } from '../../networks/api.js';
import { useNhom } from '../../team/api.js';
import { KhoiChuaCoKenh, Nut, PageHeader, StatusBadge, Tabs } from '../../../ui/index.js';
import { useNguonLuuLuong, useVideoNoiBat, type BoLocPhanTich } from '../api.js';
import { ChipPhamVi } from '../components/trangHieuSuat/ChipPhamVi.js';
import { TabDoanhThu } from '../components/trangHieuSuat/TabDoanhThu.js';
import { TabNguoiXem } from '../components/trangHieuSuat/TabNguoiXem.js';
import { TabNoiDung } from '../components/trangHieuSuat/TabNoiDung.js';
import { TabTongQuan } from '../components/trangHieuSuat/TabTongQuan.js';
import {
  useChiSoHieuSuat,
  useChuoiChiSo,
  useCoKenhToiQuanLy,
  useDoanhThuNhieuKenh,
  useNguoiXemNhieuKenh,
  useNoiDungNhieuKenh,
  useSoSanhKenh,
} from '../trangHieuSuatApi.js';

/**
 * Trang Hiệu suất (`/phan-tich`) — **bốn tab**, một chip phạm vi, một ô chọn kỳ.
 *
 * | Tab | Nội dung |
 * |---|---|
 * | Tổng quan | tám ô chỉ số bấm được dẫn biểu đồ và khung so sánh kênh |
 * | Nội dung | bốn ô, phễu lượt hiển thị, loại nội dung, video hàng đầu |
 * | Người xem | quốc gia, độ tuổi/giới tính, thiết bị, người đăng ký |
 * | Doanh thu | bốn ô, cột theo ngày, theo kênh, theo nguồn tiền |
 *
 * Trang này chỉ còn việc của chính nó: tiêu đề, chip phạm vi, thanh tab, và
 * chọn tab nào thì gọi hook nào. Bố cục của từng tab nằm trong
 * `components/trangHieuSuat/Tab*.tsx`.
 *
 * ## Mỗi tab chỉ gọi hook của chính nó
 *
 * `enabled` của mọi hook gắn với `tab === '<mã>'`. Mở tab Tổng quan thì không
 * có request nào của ba tab kia, và ngược lại — chuyển tab mới nạp. Nạp sẵn cả
 * bốn là bốn lần số truy vấn cho một trang mà người ta thường chỉ xem một tab.
 *
 * ## Bốn thứ nằm trong URL
 *
 * `?ky=` (ô chọn kỳ chung ở thanh trên), `?pham-vi=`, `?tab=` và `?chi-so=`.
 * Dán link ra là người khác mở đúng cái mình đang xem.
 *
 * `?chi-so=` **chỉ có nghĩa ở tab Tổng quan** — ba tab kia có khối riêng, không
 * khối nào đổi theo chỉ số. Nhưng nó vẫn được giữ trên URL khi chuyển tab, để
 * quay lại Tổng quan là đúng ô cũ.
 *
 * ## Phạm vi lọc ở **máy chủ**
 *
 * Giao diện chỉ gửi mã (`tat-ca`, `toi-quan-ly`, `network:<id>`, `nhom:<id>`,
 * `kenh:<id>`); danh sách kênh suy ở API rồi giao với kênh được giao
 * (`analytics.layKenhTheoPhamVi`, fail-closed). Quản lý kênh bấm "Tất cả kênh"
 * nghĩa là tất cả kênh **được giao** cho họ.
 *
 * ## Hai ô tiền
 *
 * Doanh thu gộp và RPM chỉ hiện khi có `revenue:view`, và `?chi-so=` của hai
 * endpoint đó bị máy chủ trả **403** cho người thiếu quyền — ẩn ở giao diện là
 * cho gọn, không phải để bảo mật.
 */

/** Giá trị `?chi-so=` hợp lệ; mã lạ rơi về ô đầu. */
function chiSoTuUrl(ma: string | null, xemDuoc: MaChiSo[]): MaChiSo {
  const dau = xemDuoc[0] ?? 'luot-xem';
  if (ma === null) return dau;
  return xemDuoc.includes(ma as MaChiSo) ? (ma as MaChiSo) : dau;
}

function sapTuUrl(ma: string | null): CachSapSoSanh {
  return ma === 'giam' || ma === 'cao' ? ma : 'tang';
}

/**
 * Giá trị `?tab=` hợp lệ; mã lạ **và** tab thiếu quyền đều rơi về Tổng quan.
 *
 * Người thiếu `revenue:view` mở link `?tab=doanh-thu` phải thấy một tab có số,
 * không phải một thanh tab trống rồi một khoảng trắng.
 */
function tabTuUrl(ma: string | null, coXemDoanhThu: boolean): TabHieuSuat {
  const hopLe = TAB_HIEU_SUAT.find((t) => t === ma);
  if (hopLe === undefined) return 'tong-quan';
  if (tabCanQuyenDoanhThu(hopLe) && !coXemDoanhThu) return 'tong-quan';
  return hopLe;
}

/**
 * Tám ô chỉ số từ đáp án `/chi-so`.
 *
 * Dùng lại `HangThe`/`KhungChiSo` của bốn tab trang kênh thay vì dựng hàng thẻ
 * thứ hai, nên hai chỗ có cùng cỡ số, cùng cách xuống hàng, cùng dòng "so với
 * …". `diem` để rỗng: biểu đồ của trang này là một request riêng theo chỉ số
 * đang chọn, không nằm trong ô.
 */
function dungThe(o: AnalyticsOverview | undefined, xemDuoc: MaChiSo[]): TheChiSo[] {
  if (!o) return [];

  const thoVeThe = (
    ma: MaChiSo,
  ): {
    giaTri: number | null;
    delta: number | null;
    nguon: TheChiSo['nguon'];
    ghiChu: string | null;
  } => {
    switch (ma) {
      case 'luot-xem':
        return {
          giaTri: o.views.chuaDuSoLieu ? null : o.views.value,
          delta: o.views.deltaPercent,
          nguon: 'analytics',
          ghiChu: null,
        };
      case 'gio-xem':
        return {
          giaTri: o.watchHours.value,
          delta: o.watchHours.deltaPercent,
          nguon: 'analytics',
          ghiChu: null,
        };
      case 'subscribers':
        return {
          giaTri: o.subscribersNet.value,
          delta: o.subscribersNet.deltaPercent,
          nguon: 'analytics',
          ghiChu: null,
        };
      case 'luot-hien-thi':
        return {
          giaTri: o.impressions.chuaDuSoLieu ? null : o.impressions.value,
          delta: o.impressions.deltaPercent,
          nguon: 'reporting',
          ghiChu: o.impressions.chuaDuSoLieu ? CHUA_CO_REPORTING : null,
        };
      case 'ctr':
        return {
          giaTri: o.ctr.chuaDuSoLieu ? null : o.ctr.value,
          delta: o.ctr.deltaPercent,
          nguon: 'reporting',
          ghiChu: o.ctr.chuaDuSoLieu ? CHUA_CO_REPORTING : null,
        };
      /*
       * Hai ô tiền: máy chủ **bỏ hẳn** trường khi người xem không có
       * `revenue:view` (rà soát 31/10/2026, mục S5), nên ở đây phải chịu được
       * `undefined`. Thực tế hai mã này chỉ vào `xemDuoc` khi có quyền, nên
       * nhánh `undefined` là lớp an toàn chứ không phải đường chạy thường.
       */
      case 'doanh-thu':
        return {
          giaTri: !o.grossRevenue || o.grossRevenue.chuaDuSoLieu ? null : o.grossRevenue.value,
          delta: o.grossRevenue?.deltaPercent ?? null,
          nguon: 'analytics',
          ghiChu: o.grossRevenue?.chuaDuSoLieu ? 'Kỳ này chưa có doanh thu khả dụng' : null,
        };
      case 'thoi-luong-xem':
        return {
          giaTri: o.averageViewDuration.value,
          delta: o.averageViewDuration.deltaPercent,
          nguon: 'analytics',
          ghiChu: null,
        };
      case 'rpm':
        return {
          giaTri: !o.rpm || o.rpm.chuaDuSoLieu ? null : o.rpm.value,
          delta: o.rpm?.deltaPercent ?? null,
          nguon: 'analytics',
          ghiChu: o.rpm?.chuaDuSoLieu ? 'Chưa đủ ngày có cả doanh thu và lượt xem' : null,
        };
    }
  };

  return xemDuoc.map((ma) => {
    const mt = MO_TA_CHI_SO[ma];
    const tho = thoVeThe(ma);

    return {
      khoa: ma,
      nhan: mt.nhan,
      giaTri: tho.giaTri,
      /*
       * `HangThe` nhận `dinhDang` của hợp đồng tab kênh, nơi `'gio'` là **phút**
       * và không có `'rpm'`. Giờ xem ở đây đã là giờ nên đi `'so'`, còn RPM đi
       * `'tien'` — cùng cách in, chỉ khác nhãn.
       */
      dinhDang:
        mt.dinhDang === 'gio'
          ? 'so'
          : mt.dinhDang === 'rpm'
            ? 'tien'
            : (mt.dinhDang as TheChiSo['dinhDang']),
      nguon: tho.nguon,
      laUocTinh: false,
      deltaPercent: tho.delta,
      binhThuong: null,
      ghiChu: tho.ghiChu,
      diem: [],
    };
  });
}

const CHUA_CO_REPORTING = 'Chỉ có từ khi app tạo reporting job cho kênh';

export function TrangPhanTich() {
  const duongDan = useDuongDanTrang('VẬN HÀNH');
  const toi = useToi();
  const quyen = toi.data?.permissions;
  const coXemKenh = coQuyen(quyen, 'channels', 'view');
  const coXemDoanhThu = coQuyen(quyen, 'revenue', 'view');
  const coXemNetwork = coQuyen(quyen, 'networks', 'view');
  const coXemNhanSu = coQuyen(quyen, 'team', 'view');

  const ky = useKyChung();
  const kyRong = ky.kyRong;
  const kyTrang = ky.ky;

  const [thamSo, datThamSo] = useSearchParams();
  const phamVi = thamSo.get('pham-vi') ?? PHAM_VI_MAC_DINH;

  /* Tám mã, bỏ hai mã tiền khi thiếu quyền — một luật, đọc từ shared. */
  const xemDuoc = useMemo<MaChiSo[]>(
    () => CHI_SO_HIEU_SUAT.filter((ma) => coXemDoanhThu || !chiSoCanQuyenDoanhThu(ma)),
    [coXemDoanhThu],
  );

  const chiSo = chiSoTuUrl(thamSo.get('chi-so'), xemDuoc);
  const sap = sapTuUrl(thamSo.get('sap'));
  const tab = tabTuUrl(thamSo.get('tab'), coXemDoanhThu);
  const [xemTatCa, datXemTatCa] = useState(false);

  const datUrl = (phan: Record<string, string>) => {
    const moi = new URLSearchParams(thamSo);
    for (const [k, v] of Object.entries(phan)) moi.set(k, v);
    datThamSo(moi, { replace: true });
  };

  /*
   * Lọc đúng một kênh ⇒ mốc dữ liệu (và nhóm "Theo năm" của ô chọn kỳ) tính
   * trên kênh đó. Báo xuống `KyChungProvider` vì ô chọn kỳ nằm ở thanh trên,
   * ngoài cây của trang này — và vì máy chủ cũng suy khoảng trên đúng mốc đó
   * (`analytics.khoangChoPhamVi`).
   */
  const { datKenhMoc } = ky;
  const kenhDangLoc = phamVi.startsWith('kenh:') ? phamVi.slice(5) : null;
  useEffect(() => {
    datKenhMoc(kenhDangLoc);
    return () => {
      datKenhMoc(null);
    };
  }, [kenhDangLoc, datKenhMoc]);

  /* Đổi chỉ số, chế độ hay tab thì thu lại: "Xem tất cả" là của bảng cũ. */
  useEffect(() => {
    datXemTatCa(false);
  }, [chiSo, sap, phamVi, tab, kyTrang?.ma]);

  const maKy = kyTrang?.ma ?? '';
  const goiDuoc = maKy !== '' && !kyRong;

  /*
   * `enabled` của mọi hook gắn với tab đang mở: mở Tổng quan thì không có
   * request nào của ba tab kia. Nạp sẵn cả bốn là bốn lần số truy vấn cho một
   * trang mà người ta thường chỉ xem một tab.
   */
  const laTongQuan = tab === 'tong-quan';

  const chiSoQ = useChiSoHieuSuat(maKy, phamVi, goiDuoc && laTongQuan);
  const chuoiQ = useChuoiChiSo(maKy, phamVi, chiSo, goiDuoc && laTongQuan);
  const soSanhQ = useSoSanhKenh(
    maKy,
    phamVi,
    chiSo,
    sap,
    xemTatCa ? 200 : SO_DONG_SO_SANH,
    goiDuoc && laTongQuan,
  );

  const noiDungQ = useNoiDungNhieuKenh(
    maKy,
    phamVi,
    xemTatCa ? 200 : SO_DONG_SO_SANH,
    goiDuoc && tab === 'noi-dung',
  );
  const nguoiXemQ = useNguoiXemNhieuKenh(maKy, phamVi, goiDuoc && tab === 'nguoi-xem');
  /* Hook doanh thu còn đòi **quyền**: endpoint trả 403 cho người thiếu nó. */
  const doanhThuQ = useDoanhThuNhieuKenh(
    maKy,
    phamVi,
    goiDuoc && tab === 'doanh-thu' && coXemDoanhThu,
  );

  /*
   * Hai khối của cột phải vẫn đi endpoint `from`/`to` cũ (nguồn lưu lượng và
   * video nổi bật chưa nhận mã phạm vi). Khoảng lấy **từ kỳ chung**, đúng bản
   * mà máy chủ cũng dùng, nên ba khối của trang nói về cùng một khoảng ngày.
   */
  const locCu = useMemo<BoLocPhanTich>(
    () => ({
      from: kyTrang?.from ?? '',
      to: kyTrang?.to ?? '',
      ky: maKy,
      ...phamViThanhLocCu(phamVi),
    }),
    [kyTrang?.from, kyTrang?.to, maKy, phamVi],
  );

  /* Hai khối này chỉ có ở tab Tổng quan. */
  const nguon = useNguonLuuLuong(laTongQuan ? locCu : { from: '', to: '' });
  const video = useVideoNoiBat(laTongQuan ? locCu : { from: '', to: '' }, 5);

  const doiTac = useDoiTac(undefined, coXemNetwork);
  const kyNhomNay = new Date().toISOString().slice(0, 7);
  const nhom = useNhom(kyNhomNay, coXemNhanSu);
  const kenhToiQuanLy = useCoKenhToiQuanLy(toi.data?.user.id, coXemKenh);

  const o = chiSoQ.data;
  const the = useMemo(() => dungThe(o, xemDuoc), [o, xemDuoc]);

  const khoangTruoc =
    o && o.prevFrom !== '' && o.prevTo !== '' ? { from: o.prevFrom, to: o.prevTo } : undefined;

  /*
   * Dòng phụ: phạm vi · tên kỳ · khoảng ngày · so với khoảng kỳ trước. Khoảng
   * kỳ trước lấy từ **đáp án của máy chủ**, không tự suy ở đây — hai nơi suy là
   * hai nơi lệch nhau (bài học 29/10/2026).
   */
  const khoangTruocTab =
    khoangTruoc ??
    (() => {
      const d = noiDungQ.data ?? nguoiXemQ.data ?? doanhThuQ.data;
      return d && d.prevFrom !== '' && d.prevTo !== ''
        ? { from: d.prevFrom, to: d.prevTo }
        : undefined;
    })();

  const dongPhu = [
    nhanPhamVi(phamVi, doiTac.data ?? [], nhom.data ?? []),
    kyTrang?.nhan ?? '',
    kyTrang?.nhanKhoang ?? '',
    nhanSoKyTruoc(kyTrang, khoangTruocTab),
  ]
    .filter((x) => x !== '')
    .join(' · ');

  /* Tab Doanh thu chỉ có mặt khi có quyền — xem `tabTuUrl`. */
  const mucTab = TAB_HIEU_SUAT.filter((t) => coXemDoanhThu || !tabCanQuyenDoanhThu(t)).map((t) => ({
    id: t,
    label: TAB_HIEU_SUAT_NHAN[t],
  }));

  /* `provisional` của **tab đang mở**: mỗi tab có đáp án riêng. */
  const tamTinh =
    (laTongQuan ? o?.provisional : undefined) ??
    (tab === 'noi-dung' ? noiDungQ.data?.provisional : undefined) ??
    (tab === 'nguoi-xem' ? nguoiXemQ.data?.provisional : undefined) ??
    (tab === 'doanh-thu' ? doanhThuQ.data?.provisional : undefined) ??
    false;

  return (
    <>
      <PageHeader
        breadcrumb={duongDan}
        title="Hiệu suất"
        phu={dongPhu}
        giaiThich="Bốn tab dùng chung phạm vi và kỳ ở thanh trên. Số liệu ba ngày gần nhất YouTube chưa chốt nên có thể còn đổi."
        actions={
          <Nut kieu="chinh" disabled title="Sắp có">
            <Sparkles size={18} aria-hidden="true" />
            AI phân tích &amp; đề xuất
          </Nut>
        }
      />

      <KhoiChuaCoKenh tenTrang="Hiệu suất" />

      <ChipPhamVi
        giaTri={phamVi}
        onDoi={(v) => {
          datUrl({ 'pham-vi': v });
        }}
        network={doiTac.data ?? []}
        nhom={nhom.data ?? []}
        coKenhToiQuanLy={(kenhToiQuanLy.data?.items.length ?? 0) > 0}
      />

      <div className="mb-4">
        <Tabs
          aria-label="Nhóm số liệu"
          items={mucTab}
          value={tab}
          onChange={(id) => {
            datUrl({ tab: id });
          }}
        />
      </div>

      {tamTinh && (
        <div className="mb-4">
          <StatusBadge tone="warn">
            Số liệu tạm tính — ba ngày gần nhất YouTube chưa chốt
          </StatusBadge>
        </div>
      )}

      {tab === 'tong-quan' && (
        <TabTongQuan
          the={the}
          chiSo={chiSo}
          onChonChiSo={(khoa) => {
            datUrl({ 'chi-so': khoa });
          }}
          kyTrang={kyTrang}
          khoangTruoc={khoangTruoc}
          chiSoQ={chiSoQ}
          chuoiQ={chuoiQ}
          soSanhQ={soSanhQ}
          nguonQ={nguon}
          videoQ={video}
          sap={sap}
          onDoiSap={(v) => {
            datUrl({ sap: v });
          }}
          maKy={maKy}
          goiDuoc={goiDuoc}
          xemTatCa={xemTatCa}
          onXemTatCa={() => {
            datXemTatCa(true);
          }}
          kyRong={kyRong}
          loiChiSo={chiSoQ.error ? chiSoQ.error.message : null}
          onThuLai={() => {
            void chiSoQ.refetch();
          }}
        />
      )}

      {tab === 'noi-dung' && (
        <TabNoiDung
          du={noiDungQ.data}
          dangTai={noiDungQ.isPending && goiDuoc}
          loi={noiDungQ.error ? noiDungQ.error.message : null}
          maKy={maKy}
          xemTatCa={xemTatCa}
          onXemTatCa={() => {
            datXemTatCa(true);
          }}
        />
      )}

      {tab === 'nguoi-xem' && (
        <TabNguoiXem
          du={nguoiXemQ.data}
          dangTai={nguoiXemQ.isPending && goiDuoc}
          loi={nguoiXemQ.error ? nguoiXemQ.error.message : null}
        />
      )}

      {tab === 'doanh-thu' && (
        <TabDoanhThu
          du={doanhThuQ.data}
          dangTai={doanhThuQ.isPending && goiDuoc}
          loi={doanhThuQ.error ? doanhThuQ.error.message : null}
          maKy={maKy}
        />
      )}
    </>
  );
}

/** Mã phạm vi → bộ lọc của hai endpoint cũ (`channelIds` / `groupId`). */
function phamViThanhLocCu(phamVi: string): { channelIds?: string[] } {
  const da = giaiMaPhamVi(phamVi);
  if (da?.loai === 'kenh') return { channelIds: [da.id] };
  return {};
}

/** Nhãn phạm vi cho dòng phụ đầu trang. */
function nhanPhamVi(
  phamVi: string,
  network: { id: string; name: string }[],
  nhom: { id: string; name: string }[],
): string {
  const da = giaiMaPhamVi(phamVi);
  if (!da) return PHAM_VI_NHAN_CO_DINH['tat-ca'];

  if (da.loai === 'tat-ca' || da.loai === 'toi-quan-ly') return PHAM_VI_NHAN_CO_DINH[da.loai];
  if (da.loai === 'network') {
    return `Network ${network.find((n) => n.id === da.id)?.name ?? ''}`.trim();
  }
  if (da.loai === 'nhom') return `Nhóm ${nhom.find((n) => n.id === da.id)?.name ?? ''}`.trim();
  return 'Một kênh';
}
